import type React from 'react';

import { Modal, ModalBody, ModalHeader, Text } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { ContractModalSource } from '#src/app/pages/ambassadors/landing/pageContext';

import { ContractForm } from './components/ContractForm';
import { ContractThanks } from './components/ContractThanks';
import { useContractForm } from './hooks/useContractForm';
import { useContractModal } from './hooks/useContractModal';
import { useTrackedClose } from './hooks/useTrackedClose';

export const ContractModal: React.FC<EmptyProps> = () => {
	const {
		data: { isOpen, source, ariaLabel, closeButtonAriaLabel },
		actions: { close },
	} = useContractModal();

	// `ContractModal` itself is always mounted by the page layout, so it never unmounts on close —
	// only its returned JSX toggles. `useContractForm`'s local state (`view`/`submitError`) is
	// therefore isolated in this child component instead, which only exists while the modal is
	// open: a fresh mount happens on every open, guaranteeing a fresh form on reopen.
	if (!isOpen) return null;

	return (
		<OpenContractModal
			source={source}
			ariaLabel={ariaLabel}
			closeButtonAriaLabel={closeButtonAriaLabel}
			close={close}
		/>
	);
};

const BODY_FOCUS_RING_GUTTER = '-mx-1 px-1';

type OpenContractModalProps = {
	source: ContractModalSource | null;
	ariaLabel: string;
	closeButtonAriaLabel: string;
	close: () => void;
};

const OpenContractModal: React.FC<OpenContractModalProps> = ({ source, ariaLabel, closeButtonAriaLabel, close }) => {
	const { translate } = useI18n();
	const {
		data: { view, schema, defaultValues, submitError },
		actions: { submit },
	} = useContractForm(source);
	const trackedClose = useTrackedClose(source, view, close);

	return (
		<Modal
			isDismissable
			isOpen
			// react-aria's overlay is only ever told about a dismissal through `onOpenChange(false)` -
			// that's the one path that fires for Esc, backdrop click, *and* the header's X button
			// (whose internal react-aria close also flows through here). The DS `Modal`'s own
			// `onClose` prop is a separate thing: it only fires from the X button specifically (wired
			// inside `ModalHeader`, which isn't even rendered in the thanks view), so it can never be
			// the single, complete tracking path - it stays wired to the raw, untracked `close` so the
			// X button doesn't double-fire the tracked event (see `useTrackedClose`).
			onOpenChange={(open) => {
				if (!open) trackedClose();
			}}
			aria-label={ariaLabel}
			onClose={close}
			closeButtonAriaLabel={closeButtonAriaLabel}
			size="small"
			data-testid="ambassadors-contract-modal"
		>
			{view === 'form' ? (
				<>
					<ModalHeader
						title={
							<span className="flex flex-col items-start gap-1 text-left">
								<Text
									as="span"
									variant="body"
									size="x-small"
									fontWeight="bold"
									className="text-stroke-brand-strong tracking-wide uppercase"
								>
									{translate('corgi.ambassadors.modal.eyebrow')}
								</Text>
								<Text as="span" variant="heading" fontWeight="bold" size="small">
									{translate('corgi.ambassadors.modal.title')}
								</Text>
							</span>
						}
						description={translate('corgi.ambassadors.modal.sub')}
					/>
					<ModalBody className={BODY_FOCUS_RING_GUTTER}>
						<ContractForm schema={schema} defaultValues={defaultValues} submitError={submitError} onSubmit={submit} />
					</ModalBody>
				</>
			) : (
				<ModalBody className={BODY_FOCUS_RING_GUTTER}>
					<ContractThanks source={source} onDone={trackedClose} />
				</ModalBody>
			)}
		</Modal>
	);
};
